import { UserSelector } from '@/components/UserSelector'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { ReportFilters, ReportStats, ReportType } from '@/lib/api/reports/reports-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAccountBalance,
    MdFunctions,
    MdPaid,
    MdPersonSearch,
} from 'react-icons/md'
import {
    DEFAULT_DATE_BASIS,
    DateBasis,
    formatCurrency,
    PRODUCTIVITY_SALE_STATUSES,
    productivityStatusFor,
    ProductivityType,
    getReportConfigs,
    toLocalDateString,
} from '../config/report-configs'
import { defaultBillingStatusFilter } from '../config/default-billing-status'
import CommissionModal, { CommissionLine } from './commission-modal'
import { KpiCard } from './kpi-card'
import { ReportPeriodFilter } from './report-filters'
import ProductivityFilters from './productivity-filters'
import ReportSection from './report-section'
import { REPORT_RESOURCE } from '../config/report-configs'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import { useAuth } from '@/store/hooks'

interface ProductivitySectionProps {
    scopeFilter?: Partial<ReportFilters>
    /**
     * Status da cobrança escolhido no topo da tela. Aqui ele governa os três tipos de
     * uma vez, como o resto dos filtros desta aba — a comissão só fecha se o recorte
     * for o mesmo em consultas, exames e procedimentos.
     */
    defaultBillingStatus?: string
}

/** Entidades cuja criação é atribuída a um usuário (`createdById`). */
type ProductivityEntity = Extract<ReportType, ProductivityType>

const ENTITIES: {
    key: ProductivityEntity
    label: string
    plural: string
    /** Particípio concordando com o gênero do rótulo ("consultas criadas"). */
    created: string
}[] = [
    { key: 'consultations', label: 'Consultas', plural: 'consultas', created: 'criadas' },
    { key: 'exams', label: 'Exames', plural: 'exames', created: 'criados' },
    { key: 'orders', label: 'Procedimentos', plural: 'procedimentos', created: 'criados' },
]

/** `ENTITIES` no vocabulário do segmento (fora da saúde, Procedimento vira Pedido). */
function translateEntities(labels: ServiceLabels): typeof ENTITIES {
    return ENTITIES.map(entity => {
        if (entity.key === 'consultations')
            return {
                ...entity,
                label: labels.Consultations,
                plural: labels.consultations,
            }
        if (entity.key === 'exams')
            return { ...entity, label: labels.Exams, plural: labels.exams }
        if (entity.key === 'orders')
            return { ...entity, label: labels.Orders, plural: labels.orders }
        return entity
    })
}

/**
 * Bases de período comuns aos três tipos. `due` e `admission` não existem aqui, e não
 * dá para oferecer uma base que parte das seções ignoraria em silêncio.
 */
const DATE_BASIS_OPTIONS: { value: DateBasis; label: string; hint?: string }[] = [
    {
        value: 'sold',
        label: 'Data da venda',
        hint: 'Aprovação do orçamento, ou o lançamento quando já nasceu agendado — o orçamento aprovado no mês entra na comissão do mês',
    },
    {
        value: 'created',
        label: 'Data de lançamento',
        hint: 'Quando o agendamento foi criado — a base para comissionar quem agenda',
    },
    {
        value: 'scheduled',
        label: 'Data agendada',
        hint: 'Quando o atendimento ocorre',
    },
    {
        value: 'paid',
        label: 'Data de pagamento',
        hint: 'Regime de caixa: só entram os atendimentos com cobrança paga',
    },
]

/** Números que cada seção publica de volta, para o card de totais e a comissão. */
interface EntityResult {
    total: number
    revenueCents: number
}

const EMPTY_RESULT: EntityResult = { total: 0, revenueCents: 0 }

function getDefaultFilters(defaultBillingStatus?: string): ReportFilters {
    const now = new Date()
    return {
        startDate: toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: toLocalDateString(now),
        // O período conta pela data da VENDA: a aprovação do orçamento, ou o lançamento
        // quando já nasceu agendado. Contado pelo lançamento, o orçamento lançado num mês
        // e aprovado no seguinte nunca entrava na comissão de quem vendeu — e a aba não
        // batia com o dashboard financeiro. Consulta não tem aprovação: cai no lançamento.
        dateBasis: 'sold',
        // Orçamento e cancelado não são venda (mesmo recorte do dashboard). Marcados à
        // vista no filtro, para quem quiser incluí-los.
        status: PRODUCTIVITY_SALE_STATUSES,
        // Os três tipos desta aba têm "Status da Cobrança"; o padrão entra em todos.
        billingStatus: defaultBillingStatusFilter(defaultBillingStatus ?? ''),
    }
}

/**
 * Produtividade por usuário: o que cada pessoa agendou no período.
 *
 * Não é um `type` próprio no backend — reusa os relatórios de consultas/exames/
 * procedimentos, injetando `createdById`. Assim a tabela, as estatísticas e a
 * exportação vêm de graça.
 *
 * **Período, base do período e filtros são únicos e governam todas as seções.** Cada
 * tipo tinha a sua própria caixa de filtros, visualmente idêntica às outras: o operador
 * preenchia a primeira e as demais continuavam trazendo tudo — cancelados inclusive —,
 * enquanto o card de totais somava as três como se fossem o mesmo recorte. Comissão
 * apurada assim sai errada sem nada denunciar. Na seção de cada tipo ficaram só os
 * filtros que os outros não têm ("é retorno?", item do catálogo, material).
 */
export default function ProductivitySection({
    scopeFilter,
    defaultBillingStatus,
}: ProductivitySectionProps) {
    const [userId, setUserId] = useState<number | null>(null)
    const [userName, setUserName] = useState('')
    const isResourceVisible = useResourceVisibility()
    const { user } = useAuth()
    const segment = user?.company?.segment || 'OTHER'
    // Só os tipos liberados no menu (plano, role, segmento) entram na escolha.
    const allowedEntities = ENTITIES.filter(item => {
        const { module, resource } = REPORT_RESOURCE[item.key]
        return isResourceVisible(module, resource)
    }).map(item => item.key)
    const [entities, setEntities] = useState<ProductivityEntity[]>(() =>
        allowedEntities.slice(0, 1)
    )
    const [filters, setFilters] = useState<ReportFilters>(() =>
        getDefaultFilters(defaultBillingStatus)
    )
    const [results, setResults] = useState<Record<string, EntityResult>>({})
    const [commissionOpen, setCommissionOpen] = useState(false)
    /**
     * Tipo em foco quando há mais de um selecionado. Empilhadas, as três listagens
     * empurravam a de baixo para fora da tela e o operador rolava sem saber onde uma
     * terminava e a outra começava.
     */
    const [activeEntity, setActiveEntity] = useState<ProductivityEntity>('consultations')
    const serviceLabels = useServiceLabel()
    const allowedKey = allowedEntities.join(',')
    // As permissões podem chegar depois da montagem: tira da seleção o que não
    // está liberado e, se ela ficar vazia, começa pelo primeiro tipo liberado.
    useEffect(() => {
        const allowed = allowedKey ? allowedKey.split(',') : []
        setEntities(prev => {
            const kept = prev.filter(key => allowed.includes(key))
            if (kept.length === prev.length && kept.length > 0) return prev
            return kept.length > 0
                ? kept
                : (allowed.slice(0, 1) as ProductivityEntity[])
        })
    }, [allowedKey])
    const translatedEntities = useMemo(
        () =>
            translateEntities(serviceLabels).filter(item =>
                allowedKey.split(',').includes(item.key)
            ),
        [serviceLabels, allowedKey]
    )
    const reportConfigs = useMemo(
        () => getReportConfigs(serviceLabels, segment),
        [serviceLabels, segment]
    )

    const selected = useMemo(
        () => translatedEntities.filter(item => entities.includes(item.key)),
        [entities, translatedEntities]
    )

    // O tipo em foco pode ter acabado de ser desmarcado; a aba volta para o primeiro
    // que restou, senão a área de listagem ficaria vazia sem explicação.
    useEffect(() => {
        if (!entities.includes(activeEntity) && entities.length > 0) {
            setActiveEntity(entities[0])
        }
    }, [entities, activeEntity])

    const toggleEntity = (key: ProductivityEntity) => {
        setEntities(prev =>
            prev.includes(key)
                ? // Nunca desmarcar o último: sem tipo não há o que exibir.
                  prev.length === 1
                    ? prev
                    : prev.filter(k => k !== key)
                : [...prev, key]
        )
    }

    /**
     * Filtros efetivos de cada tipo: os comuns, com a situação traduzida para o
     * vocabulário daquele enum.
     *
     * O valor é `null` quando o operador escolheu apenas situações que o tipo não tem
     * (ex.: "Não compareceu" para exames). Não é o mesmo que "sem filtro": mandar a
     * requisição sem status traria o tipo inteiro, e o card somaria registros que o
     * recorte pedia para excluir.
     *
     * ⚠️ **Memoizado, e isto não é otimização — é correção.** Estes objetos são o
     * `scopeFilter` de cada `ReportSection`, que o usa nas dependências de `loadData` e
     * `loadStats`. Montá-los durante o render (era uma função chamada no JSX) dava a
     * cada passagem uma identidade nova: o efeito refazia a requisição, a resposta
     * chamava `setState`, o re-render criava outro objeto — e a tela entrava em laço
     * infinito de requisições assim que a primeira seção montava.
     */
    const filtersByType = useMemo(() => {
        const out: Partial<Record<ProductivityEntity, Partial<ReportFilters> | null>> = {}

        for (const item of ENTITIES) {
            const status = productivityStatusFor(item.key, filters.status as string[])
            out[item.key] =
                filters.status?.length && status?.length === 0
                    ? null
                    : {
                          ...(scopeFilter || {}),
                          ...filters,
                          status,
                          createdById: userId ?? undefined,
                      }
        }

        return out
    }, [filters, scopeFilter, userId])

    // Estáveis por entidade: os callbacks entram no `deps` das cargas do `ReportSection`.
    const [handleTotalChange, handleStatsChange] = useMemo(() => {
        const totalHandlers: Record<string, (total: number) => void> = {}
        const statsHandlers: Record<string, (stats: ReportStats | null) => void> = {}

        for (const item of ENTITIES) {
            totalHandlers[item.key] = total =>
                setResults(prev => ({
                    ...prev,
                    [item.key]: { ...(prev[item.key] ?? EMPTY_RESULT), total },
                }))
            statsHandlers[item.key] = stats =>
                setResults(prev => ({
                    ...prev,
                    [item.key]: {
                        ...(prev[item.key] ?? EMPTY_RESULT),
                        revenueCents: stats?.totalRevenue ?? 0,
                    },
                }))
        }

        return [totalHandlers, statsHandlers] as const
    }, [])

    // Só os tipos selecionados entram na conta — o resultado de um tipo desmarcado
    // continua no estado (a seção nem monta mais para republicar zero). O tipo cujo
    // filtro de situação não se aplica entra zerado: ele não tem o que comissionar.
    const lines: CommissionLine[] = useMemo(
        () =>
            selected.map(item => {
                const applies = filtersByType[item.key] !== null
                return {
                    key: item.key,
                    label: item.plural,
                    total: applies ? results[item.key]?.total ?? 0 : 0,
                    revenueCents: applies
                        ? results[item.key]?.revenueCents ?? 0
                        : 0,
                }
            }),
        [selected, results, filtersByType]
    )

    const totalCount = lines.reduce((sum, line) => sum + line.total, 0)
    const totalRevenueCents = lines.reduce((sum, line) => sum + line.revenueCents, 0)

    const handlePeriodChange = useCallback(
        (range: { startDate: string; endDate: string }) =>
            setFilters(prev => ({ ...prev, ...range })),
        []
    )

    const handleDateBasisChange = useCallback(
        (basis: DateBasis) => setFilters(prev => ({ ...prev, dateBasis: basis })),
        []
    )

    /**
     * Período no formato do documento. `YYYY-MM-DD` é o formato do filtro, não o que
     * se lê num demonstrativo — e a data é montada por partes, sem `new Date()`, que
     * interpretaria a string como UTC e voltaria um dia em Brasília.
     */
    const periodLabel = useMemo(() => {
        const toBr = (value?: string) => {
            if (!value) return ''
            const [year, month, day] = value.split('-')
            return day ? `${day}/${month}/${year}` : value
        }
        const start = toBr(filters.startDate)
        const end = toBr(filters.endDate)
        if (!start && !end) return undefined
        return start && end ? `${start} a ${end}` : start || end
    }, [filters.startDate, filters.endDate])

    return (
        <div className="flex flex-col gap-5">
            <div className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* O escopo vem do seletor do topo da página: sem isto o selector
                        listaria só a unidade ativa, enquanto a tabela já mostra os
                        registros de todas as filiais — e o usuário de outra filial ficaria
                        impossível de escolher. */}
                    <UserSelector
                        value={userId}
                        onChange={(id, user) => {
                            setUserId(id)
                            setUserName(user?.name || '')
                        }}
                        label="Usuário"
                        placeholder="Buscar usuário..."
                        scope={scopeFilter?.scope === 'organization' ? 'organization' : 'company'}
                        companyId={scopeFilter?.companyId}
                    />

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">Tipos de atendimento</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {translatedEntities.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`btn btn-sm ${
                                        entities.includes(item.key)
                                            ? 'btn-secondary'
                                            : 'btn-ghost border border-base-300'
                                    }`}
                                    onClick={() => toggleEntity(item.key)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <span className="mt-2 text-xs text-base-content/50">
                            Selecione mais de um para somar a comissão dos tipos.
                        </span>
                    </div>
                </div>
            </div>

            {!userId ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-12 flex flex-col items-center gap-3 text-center">
                    <MdPersonSearch className="text-4xl text-base-content/30" />
                    <p className="font-medium text-base-content">
                        Selecione um usuário
                    </p>
                    <p className="text-sm text-base-content/60">
                        Escolha quem você quer analisar para ver os agendamentos que essa
                        pessoa criou no período.
                    </p>
                </div>
            ) : (
                <>
                    {/* Período, base do período e filtros valem para todas as seções: é
                        o que garante que a comissão some o mesmo recorte em cada tipo. */}
                    <ReportPeriodFilter
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onChange={handlePeriodChange}
                        dateBasis={filters.dateBasis || 'sold'}
                        dateBasisOptions={DATE_BASIS_OPTIONS}
                        onDateBasisChange={handleDateBasisChange}
                    />

                    <ProductivityFilters
                        filters={filters}
                        onChange={setFilters}
                        dataScope={{
                            scope: scopeFilter?.scope,
                            companyId: scopeFilter?.companyId,
                        }}
                    />

                    {/* Soma dos tipos selecionados, no MESMO desenho dos indicadores
                        de cada seção (`KpiCard`): números da mesma natureza lado a lado
                        com aparências diferentes fazem o leitor achar que medem coisas
                        diferentes. */}
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            <KpiCard
                                title="Total Geral"
                                value={totalCount}
                                icon={<MdFunctions />}
                                accent="primary"
                                hint={
                                    selected.length > 1
                                        ? `${selected.length} tipos somados`
                                        : `${selected[0].plural} no período`
                                }
                            />
                            <KpiCard
                                title="Vendido"
                                value={formatCurrency(totalRevenueCents)}
                                icon={<MdAccountBalance />}
                                accent="success"
                                hint="apurada no período"
                            />
                            <div className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex items-center shadow-sm">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm gap-2 w-full"
                                    disabled={totalCount === 0}
                                    onClick={() => setCommissionOpen(true)}
                                >
                                    <MdPaid className="text-lg" />
                                    Calcular comissão
                                </button>
                            </div>
                        </div>

                        {selected.length > 1 && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-base-content/60 px-1">
                                {lines.map(line => (
                                    <span key={line.key}>
                                        <span className="font-medium text-base-content">
                                            {line.total}
                                        </span>{' '}
                                        {line.label} ·{' '}
                                        {formatCurrency(line.revenueCents)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Abas por tipo. Com dois ou três selecionados, as listagens vinham
                        empilhadas e a de baixo ficava fora da primeira dobra. */}
                    {selected.length > 1 && (
                        <div
                            role="tablist"
                            className="flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-base-300/70 -mx-1 px-1"
                        >
                            {selected.map(item => {
                                const isActive = item.key === activeEntity
                                const count = filtersByType[item.key]
                                    ? results[item.key]?.total ?? 0
                                    : 0
                                return (
                                    <button
                                        key={item.key}
                                        role="tab"
                                        aria-selected={isActive}
                                        onClick={() => setActiveEntity(item.key)}
                                        className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                            isActive
                                                ? 'border-secondary text-secondary'
                                                : 'border-transparent text-base-content/60 hover:text-base-content hover:border-base-300'
                                        }`}
                                    >
                                        {item.label}
                                        <span
                                            className={`badge badge-sm border-0 ${
                                                isActive
                                                    ? 'bg-secondary text-secondary-content'
                                                    : 'bg-base-200 text-base-content/60'
                                            }`}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    {/* ⚠️ TODAS as seções ficam montadas; a aba inativa é apenas
                        escondida. São elas que publicam total e receita
                        (`onTotalChange`/`onStatsChange`), e é dessa soma que saem o
                        Total Geral e a comissão — montar só a ativa faria a comissão
                        sair pelo tipo em foco, sem nada denunciar. */}
                    {selected.map(item => {
                        const effective = filtersByType[item.key]
                        const hidden = selected.length > 1 && item.key !== activeEntity

                        if (!effective) {
                            return (
                                <div
                                    key={item.key}
                                    className={`bg-base-100 rounded-xl border border-base-300/50 p-6 text-sm text-base-content/60 ${
                                        hidden ? 'hidden' : ''
                                    }`}
                                >
                                    <span className="font-medium text-base-content">
                                        {item.label}
                                    </span>{' '}
                                    ficaram de fora: nenhuma das situações escolhidas
                                    existe neste tipo de atendimento.
                                </div>
                            )
                        }

                        return (
                            <div key={item.key} className={hidden ? 'hidden' : ''}>
                                <ReportSection
                                    reportType={item.key}
                                    config={reportConfigs[item.key]}
                                    scopeFilter={effective}
                                    title={`${item.label} ${item.created} por ${userName || 'usuário'}`}
                                    onTotalChange={handleTotalChange[item.key]}
                                    onStatsChange={handleStatsChange[item.key]}
                                    hidePeriodFilter
                                    hideCommonFilters
                                />
                            </div>
                        )
                    })}

                    <CommissionModal
                        isOpen={commissionOpen}
                        onClose={() => setCommissionOpen(false)}
                        lines={lines}
                        userId={userId}
                        userName={userName || 'O usuário'}
                        entries={selected
                            .map(item => ({
                                type: item.key,
                                label: item.plural,
                                filters: filtersByType[item.key] ?? null,
                            }))
                            .filter(
                                (entry): entry is {
                                    type: ProductivityEntity
                                    label: string
                                    filters: Partial<ReportFilters>
                                } => entry.filters !== null
                            )}
                        periodLabel={periodLabel}
                    />
                </>
            )}
        </div>
    )
}
